import { useMemo } from "react";
import { getRequiredCaseInfoFields } from "../utils/accessioningClientRequirements";
import type { DateOnlyString } from "../utils/dateOnly";

interface SectionCompletion {
  completed: number;
  total: number;
  requiredComplete: boolean;
  /** For non-blocking sections, indicates if recommended fields are filled (shows warning if false) */
  recommendedComplete?: boolean;
}

export interface FieldCompletionResult {
  patient: SectionCompletion;
  primaryInsurance: SectionCompletion;
  secondaryInsurance: SectionCompletion;
  caseInfo: SectionCompletion;
  specimens: SectionCompletion;
}

interface FormValues {
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: DateOnlyString | null;
    sex: string;
    medicalRecordNumber: string;
    mobilePhone?: string;
    homePhone?: string;
    workPhone?: string;
    email?: string;
    address?: {
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  case: {
    accountId?: string;
    locationId?: string;
    physicianId?: string;
    procedureId?: string;
    dateReceived?: DateOnlyString | null;
    dateCollected?: DateOnlyString | null;
    accessioningType?: string;
    externalCaseNumber?: string;
    consultationReason?: string;
    clinicalHistoryCodes?: string[];
    clinicalHistoryNotes?: string;
    primaryInsurance?: {
      insuranceProvider?: string;
      planType?: string;
      policyNumber?: string;
      authorizationNumber?: string;
      groupNumber?: string;
      groupName?: string;
      patientRelationToInsured?: string;
      address?: {
        address?: string;
        city?: string;
        state?: string;
        zipCode?: string;
      };
    };
    secondaryInsurance?: {
      insuranceProvider?: string;
      planType?: string;
      policyNumber?: string;
      authorizationNumber?: string;
      groupNumber?: string;
      groupName?: string;
      patientRelationToInsured?: string;
      address?: {
        address?: string;
        city?: string;
        state?: string;
        zipCode?: string;
      };
    };
    specimens?: Array<{
      number?: string;
      sourceId?: string;
      subsiteId?: string | null;
      collectionMethod?: string;
      ruleOuts?: string;
      comments?: string;
    }>;
  };
}

function countFilledFields(obj: Record<string, any>, fields: string[]): number {
  return fields.filter((field) => {
    const value = obj?.[field];
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  }).length;
}

export function useFieldCompletion(
  formValues: FormValues
): FieldCompletionResult {
  return useMemo(() => {
    // Patient section
    const patientRequiredFields = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "sex",
      "medicalRecordNumber",
    ];
    const patientAllFields = [
      ...patientRequiredFields,
      "mobilePhone",
      "homePhone",
      "workPhone",
      "email",
    ];
    const patientCompleted = countFilledFields(
      formValues.patient,
      patientAllFields
    );
    const patientRequiredCompleted = countFilledFields(
      formValues.patient,
      patientRequiredFields
    );

    // Primary Insurance section
    // Note: Insurance is NOT required per C-783/C-1617 - it's "recommended" with non-blocking error tracking
    const primaryInsRecommendedFields = ["insuranceProvider", "policyNumber"];
    const primaryInsAllFields = [
      ...primaryInsRecommendedFields,
      "planType",
      "authorizationNumber",
      "groupNumber",
      "groupName",
      "patientRelationToInsured",
    ];
    const primaryInsCompleted = countFilledFields(
      formValues.case?.primaryInsurance || {},
      primaryInsAllFields
    );
    const primaryInsRecommendedCompleted = countFilledFields(
      formValues.case?.primaryInsurance || {},
      primaryInsRecommendedFields
    );

    // Secondary Insurance section (no required fields)
    const secondaryInsAllFields = [
      "insuranceProvider",
      "planType",
      "policyNumber",
      "authorizationNumber",
      "groupNumber",
      "groupName",
      "patientRelationToInsured",
    ];
    const secondaryInsCompleted = countFilledFields(
      formValues.case?.secondaryInsurance || {},
      secondaryInsAllFields
    );

    const aType = formValues.case?.accessioningType;
    const caseInfoRequiredFields = getRequiredCaseInfoFields(aType);

    const caseInfoAllFields = [
      ...caseInfoRequiredFields,
      "clinicalHistoryNotes",
    ];
    const caseInfoCompleted = countFilledFields(
      formValues.case || {},
      caseInfoAllFields
    );
    const caseInfoRequiredCompleted = countFilledFields(
      formValues.case || {},
      caseInfoRequiredFields
    );

    // Specimens section
    const specimens = formValues.case?.specimens || [];
    const specimensTotal = specimens.length > 0 ? specimens.length : 1; // At least 1 specimen required
    const specimensCompleted = specimens.filter(
      (spec) => !!spec.sourceId
    ).length;
    const specimensRequiredComplete =
      specimens.length > 0 && specimensCompleted === specimens.length;

    return {
      patient: {
        completed: patientCompleted,
        total: patientAllFields.length,
        requiredComplete:
          patientRequiredCompleted === patientRequiredFields.length,
      },
      primaryInsurance: {
        completed: primaryInsCompleted,
        total: primaryInsAllFields.length,
        // Insurance is NOT required (non-blocking per C-783/C-1617), always true
        requiredComplete: true,
        // Track recommended fields for warning display (yellow badge)
        recommendedComplete:
          primaryInsRecommendedCompleted === primaryInsRecommendedFields.length,
      },
      secondaryInsurance: {
        completed: secondaryInsCompleted,
        total: secondaryInsAllFields.length,
        requiredComplete: true, // No required fields in secondary insurance
      },
      caseInfo: {
        completed: caseInfoCompleted,
        total: caseInfoAllFields.length,
        requiredComplete:
          caseInfoRequiredCompleted === caseInfoRequiredFields.length,
      },
      specimens: {
        completed: specimensCompleted,
        total: specimensTotal,
        requiredComplete: specimensRequiredComplete,
      },
    };
  }, [formValues]);
}
